import { Component } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation, OrFilter } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { AddressesService } from 'src/app/modules/common/_services/addresses.service';
import { CountriesService } from 'src/app/modules/common/_services/countries.service';
import { MediatorsService } from 'src/app/modules/mediators/_services/mediators.service';
import { Mediator } from 'src/app/modules/mediators/_types/mediator';
import { LoginService } from 'src/app/modules/login/login.service';

@Component({
    selector: 'app-case-mediator-create-new',
    templateUrl: './create-new.component.html',
    styleUrls: ['./create-new.component.scss'],
    standalone: false
})
export class CaseMediatorCreateNewComponent {
  public mediatorFormGroup!: RxFormGroup;
  public mediator: Mediator = new Mediator();
  public allowWrite: boolean = false;
  public allowDelete: boolean = false;
  public countries: any = [];

  constructor(
    private mediatorsService: MediatorsService,
    private addressesService: AddressesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private dynamicDialogRef: DynamicDialogRef,
    private loginService: LoginService
  ) {
    this.mediatorFormGroup = this.formBuilder.formGroup<Mediator>(this.mediator) as RxFormGroup;
  }

  async ngOnInit() {
    this.loadCountries();
    this.allowWrite = this.loginService.hasPermission("CASES.WRITE");
    this.allowDelete = this.loginService.hasPermission("CASES.DELETE");
  }

  async loadCountries() {
    let params: IParamsCollection = { sort: ['name'] };
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.mediatorFormGroup);
    if (valid) {
      this.mediator = Object.assign(this.mediator, this.mediatorFormGroup.value);

      let hasDuplicated = await this.hasDuplicatedMediator();
      if (hasDuplicated) {
        this.confirmationService.confirm({
          message: "Ya existe un mediador con el mismo nombre, teléfono o email. ¿Desea crearlo igualmente?",
          header: "Mediador duplicado",
          acceptLabel: "Crear nuevo",
          rejectLabel: "Cancelar",
          icon: 'pi pi-exclamation-triangle',
          accept: async () => {
            await this.persistMediator();
          },
          reject: () => {
            this.dynamicDialogRef.close();
          }
        });
        return;
      }

      await this.persistMediator();
    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  private async hasDuplicatedMediator(): Promise<boolean> {
    let name = this.mediator.name?.trim();
    let phone1 = this.mediator.phone1?.trim();
    let email = this.mediator.email?.trim();

    let duplicateFilters: any[] = [];
    if (name) duplicateFilters.push(new Filter(FilterOperation.equals, 'name', '', name));
    if (phone1) duplicateFilters.push(new Filter(FilterOperation.equals, 'phone1', '', phone1));
    if (email) duplicateFilters.push(new Filter(FilterOperation.equals, 'email', '', email));

    if (duplicateFilters.length === 0) return false;

    let params: IParamsCollection = {
      page: { number: 1, size: 1 },
      filter: FilterBuilder.fromObject({ duplicated: new OrFilter(duplicateFilters) }).build()
    };

    let response = await firstValueFrom(this.mediatorsService.all(params));
    return (response.meta?.['total'] || 0) > 0;
  }

  private async persistMediator() {
    if (this.mediator.address != null) {
      let address: any = await firstValueFrom(this.addressesService.save(this.mediator.address));
      this.mediator.addressId = address.id;
    }

    let mediatorItem = await firstValueFrom(this.mediatorsService.save(this.mediator));
    this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Mediador guardado correctamente." });
    this.dynamicDialogRef.close(mediatorItem);
  }

  cancel() {
    this.dynamicDialogRef.close();
  }
}
